import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";

const ViewOrderDetails = ({ rfq, closeView }) => {
  const navigate = useNavigate();
  const [currentDate, setCurrentDate] = useState(new Date());

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentDate(new Date());
    }, 1000);

    return () => clearInterval(interval);
  }, []);

  const formatDate = (dateString) => {
    if (!dateString || isNaN(new Date(dateString).getTime())) {
      return "N/A";
    }
    return new Date(dateString)
      .toLocaleDateString("en-IN", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      })
      .replace(/ /g, " ");
  };

  const requestedDate = rfq.createdAt ? formatDate(rfq.createdAt) : "N/A";
  const quotationReceivedDate = rfq.quatation?.quatationDate ? formatDate(rfq.quatation.quatationDate) : "N/A";
  const readyToPackDate = rfq.createdAt ? formatDate(rfq.createdAt) : "N/A";
  const expectedDelivery = rfq.createdAt ? new Date(rfq.createdAt) : null;
  const formattedDelivery = expectedDelivery
    ? expectedDelivery
        .toLocaleDateString("en-IN", {
          day: "2-digit",
          month: "short",
          year: "numeric",
        })
        .replace(/ /g, " ")
    : "N/A";

  const StatusItem = ({ label, date, color }) => (
    <li className={`flex justify-between items-center ${color} p-2 rounded`}>
      <span className="text-gray-700">{label}</span>
      <span className="ml-2 font-medium text-gray-900">{date}</span>
    </li>
  );

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 px-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-[100%] md:max-w-5xl max-h-[90vh] overflow-y-auto p-6">
        {/* Header */}
        <div className="flex items-center mb-6 justify-between">
          <div className="flex gap-10 items-center">
            <button
              className="text-[#2b6e73] border border-[#2b6e73] rounded-full w-9 h-9 flex items-center justify-center"
              onClick={closeView}
            >
              ←
            </button>
            <h2 className="text-2xl font-bold text-[#2b6e73]">
              Sales Enquiry Management{" "}
              <span className="text-base font-normal text-gray-500">
                - {rfq.status || "N/A"}
              </span>
            </h2>
          </div>
        </div>

        {/* Info Grid with Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {/* Enquiry Information Card */}
          <div className="bg-white p-4 rounded-lg shadow">
            <h3 className="text-lg font-semibold mb-2 text-[#2b6e73]">Enquiry Information</h3>
            <div className="grid grid-cols-1 gap-1 text-sm">
              <div className="flex items-center">
                <span className="font-semibold text-gray-600 min-w-[120px]">Order Status</span>
                <span className="w-[10px] text-gray-600">:</span>
                <span className="font-medium text-gray-800">{rfq.status || "N/A"}</span>
              </div>
              <div className="flex items-center">
                <span className="font-semibold text-gray-600 min-w-[120px]">RFQ No</span>
                <span className="w-[10px] text-gray-600">:</span>
                <span className="font-medium text-gray-800">{rfq.rfqNo || "N/A"}</span>
              </div>
              <div className="flex items-center">
                <span className="font-semibold text-gray-600 min-w-[120px]">Quotation No</span>
                <span className="w-[10px] text-gray-600">:</span>
                <span className="font-medium text-gray-800">{rfq.quotationNo ? `QT${rfq.quotationNo}` : "N/A"}</span>
              </div>
            </div>
          </div>

          {/* Customer Information Card */}
          <div className="bg-white p-4 rounded-lg shadow min-w-0 overflow-hidden">
            <h3 className="text-lg font-semibold mb-2 text-[#2b6e73]">Customer Information</h3>
            <div className="grid grid-cols-1 gap-1 text-sm">
              <div className="flex items-start">
                <span className="font-semibold text-gray-600 min-w-[120px]">Name</span>
                <span className="w-[10px] text-gray-600">:</span>
                <span className="font-medium text-gray-800 break-words">{rfq.customerName || "N/A"}</span>
              </div>
              <div className="flex items-start">
                <span className="font-semibold text-gray-600 min-w-[120px]">Company</span>
                <span className="w-[10px] text-gray-600">:</span>
                <span className="font-medium text-gray-800 break-words">{rfq.companyName || "N/A"}</span>
              </div>
              <div className="flex items-start">
                <span className="font-semibold text-gray-600 min-w-[120px]">Mobile Number</span>
                <span className="w-[10px] text-gray-600">:</span>
                <span className="font-medium text-gray-800">{rfq.mobileNumber || "N/A"}</span>
              </div>
              <div className="flex items-start">
                <span className="font-semibold text-gray-600 min-w-[120px]">Email</span>
                <span className="w-[10px] text-gray-600">:</span>
                <span className="font-medium text-gray-800 break-words max-w-[60%] sm:max-w-[150px] overflow-hidden">
                  {rfq.customerEmail || "N/A"}
                </span>
              </div>
              <div className="flex items-start">
                <span className="font-semibold text-gray-600 min-w-[120px]">Address</span>
                <span className="w-[10px] text-gray-600">:</span>
                <span className="font-medium text-gray-800 break-words">{rfq.address || "N/A"}</span>
              </div>
            </div>
          </div>

          {/* Enquiry Status */}
          <div>
            <h3 className="text-lg font-semibold text-[#2b6e73] mb-2">Enquiry Status</h3>
            <ul className="space-y-2">
              <StatusItem label="Requested" date={requestedDate} color="bg-green-100" />
              <StatusItem label="Quotation Received" date={quotationReceivedDate} color="bg-gray-100" />
              <StatusItem label="Rejected" date="N/A" color="bg-red-100" />
            </ul>
          </div>
        </div>

        {/* Order Items */}
        <div className="mt-8">
          <h3 className="text-lg font-semibold mb-2">Order Items</h3>
          <div className="overflow-x-auto">
            <table className="min-w-full border text-sm text-center">
              <thead className="bg-[#2b6e73] text-white">
                <tr>
                  <th className="p-3">Product</th>
                  <th className="p-3">CAS Number</th>
                  <th className="p-3">Catalog Number</th>
                  <th className="p-3">HSN Code</th>
                  <th className="p-3">Quantity</th>
                </tr>
              </thead>
              <tbody className="bg-white">
                <tr className="border-b hover:bg-gray-50">
                  <td className="p-3">{rfq.productName || "N/A"}</td>
                  <td className="p-3">{rfq.casNumber || "N/A"}</td>
                  <td className="p-3">{rfq.catalogNumber || "N/A"}</td>
                  <td className="p-3">{rfq.hsCode || "N/A"}</td>
                  <td className="p-3">{rfq.quantity || "N/A"}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        {/* Footer Action */}
        <div className="mt-6 justify-start">
          <button
            className="text-[#2b6e73] border border-[#2b6e73] px-4 py-2 rounded hover:bg-green-50"
            onClick={closeView}
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default ViewOrderDetails;